/**
 * Admin · HustlePlaza Pricing & Freight Console
 *
 * Single dashboard for the marketplace owner to:
 *   1. Tune markup % + min margin % (global + per-category)
 *   2. Manage competitor prices per SKU
 *   3. Edit freight zones / per-kg rates / free-shipping thresholds
 *   4. See live catalog health (competitive vs uncompetitive counts)
 *
 * Mounted at `/admin/plaza-pricing` for admins only.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Save, RefreshCw, Plus, Trash2, AlertCircle, Tag, Truck,
  TrendingUp, CheckCircle2, Loader2, Sparkles,
} from "lucide-react";
import "./PlazaAdminPricingPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API, withCredentials: true });

export default function PlazaAdminPricingPage() {
  const [tab, setTab] = useState("markup");
  const [authError, setAuthError] = useState(null);

  // Eager auth probe — gives a friendly screen instead of a runtime overlay
  // when the admin opens this URL without a session.
  useEffect(() => {
    api.get("/api/auth/me").catch((e) => {
      if (e?.response?.status === 401) setAuthError("unauthenticated");
    });
  }, []);

  if (authError === "unauthenticated") {
    return (
      <div className="hpz-admin" data-testid="admin-pricing-auth-gate">
        <header className="hpz-admin__head">
          <h1>HustlePlaza · Pricing & Freight</h1>
          <p>Owner controls — markup, competitor matching, and freight zones</p>
        </header>
        <main className="hpz-admin__main">
          <section className="hpz-admin__panel">
            <p className="hpz-admin__notice">
              <AlertCircle size={14} />
              You need to be signed in as an agency owner to view this page.
              {" "}<a href="/login?next=/admin/plaza-pricing">Sign in</a>.
            </p>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="hpz-admin">
      <header className="hpz-admin__head">
        <h1>HustlePlaza · Pricing & Freight</h1>
        <p>Owner controls — markup, competitor matching, and freight zones</p>
      </header>
      <nav className="hpz-admin__tabs">
        {[
          { k: "markup",     label: "Markup & margin", icon: TrendingUp },
          { k: "competitor", label: "Competitor prices", icon: Tag },
          { k: "freight",    label: "Freight zones", icon: Truck },
          { k: "themes",     label: "Holiday themes", icon: Sparkles },
        ].map(t => (
          <button key={t.k}
                  className={`hpz-admin__tab ${tab === t.k ? "active" : ""}`}
                  onClick={() => setTab(t.k)}
                  data-testid={`admin-tab-${t.k}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </nav>
      <main className="hpz-admin__main">
        {tab === "markup"     && <MarkupPanel />}
        {tab === "competitor" && <CompetitorPanel />}
        {tab === "freight"    && <FreightPanel />}
        {tab === "themes"     && <ThemesPanel />}
      </main>
    </div>
  );
}

/* ─── Markup & margin ─────────────────────────────────────────────── */
function MarkupPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get("/api/plaza/pricing/config")
       .then(r => setCfg(r.data))
       .catch(() => setCfg({ error: true }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/api/plaza/pricing/config", {
        default_markup_pct: parseFloat(cfg.default_markup_pct),
        min_margin_pct: parseFloat(cfg.min_margin_pct),
        category_overrides: cfg.category_overrides || {},
      });
      setSavedAt(new Date());
      // refresh stats
      const r2 = await api.get("/api/plaza/pricing/config");
      setCfg(r2.data);
      // Show recompute summary
      alert(`Config saved. Recomputed ${r.data.recomputed.toLocaleString()} products — ${r.data.uncompetitive.toLocaleString()} now uncompetitive (hidden from storefront).`);
    } finally {
      setSaving(false);
    }
  };

  const recompute = async () => {
    setSaving(true);
    try {
      const r = await api.post("/api/plaza/pricing/recompute");
      alert(`Recomputed ${r.data.recomputed.toLocaleString()} products.`);
      const r2 = await api.get("/api/plaza/pricing/config");
      setCfg(r2.data);
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="hpz-admin__loading"><Loader2 className="hpz-spin" /></div>;
  if (cfg.error) return (
    <section className="hpz-admin__panel"><p className="hpz-admin__error">
      <AlertCircle size={14} /> Failed to load pricing config. Please re-authenticate.
    </p></section>
  );

  const stats = cfg.stats || {};
  return (
    <section className="hpz-admin__panel">
      <div className="hpz-admin__stats" data-testid="admin-pricing-stats">
        <Stat label="Catalog total" value={stats.products_total} />
        <Stat label="Competitive" value={stats.products_competitive}
              accent="green" />
        <Stat label="Hidden (uncompetitive)" value={stats.products_uncompetitive}
              accent="red" />
        <Stat label="With competitor data" value={stats.products_with_competitor_data}
              accent="blue" />
      </div>

      <div className="hpz-admin__form">
        <label>
          <span>Default markup %</span>
          <input type="number" step="0.5" min="0" max="500"
                 value={cfg.default_markup_pct}
                 onChange={e => setCfg({ ...cfg, default_markup_pct: e.target.value })}
                 data-testid="admin-input-markup" />
          <small>Applied as: <code>cost × (1 + markup%/100)</code></small>
        </label>
        <label>
          <span>Min margin % (profitability floor)</span>
          <input type="number" step="0.5" min="0" max="100"
                 value={cfg.min_margin_pct}
                 onChange={e => setCfg({ ...cfg, min_margin_pct: e.target.value })}
                 data-testid="admin-input-min-margin" />
          <small>Products competing below <code>cost × (1 + min/100)</code> are auto-hidden.</small>
        </label>
      </div>

      <div className="hpz-admin__actions">
        <button onClick={save} disabled={saving} className="hpz-admin__btn primary"
                data-testid="admin-save-pricing">
          {saving ? <Loader2 className="hpz-spin" size={14} /> : <Save size={14} />}
          Save & recompute catalog
        </button>
        <button onClick={recompute} disabled={saving} className="hpz-admin__btn"
                data-testid="admin-recompute">
          <RefreshCw size={14} /> Recompute now (no config change)
        </button>
        {savedAt && (
          <span className="hpz-admin__saved-at">
            <CheckCircle2 size={14} /> Saved {savedAt.toLocaleTimeString()}
          </span>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`hpz-admin__stat ${accent || ""}`}>
      <div className="hpz-admin__stat-value">
        {(value ?? 0).toLocaleString()}
      </div>
      <div className="hpz-admin__stat-label">{label}</div>
    </div>
  );
}

/* ─── Competitor entry ────────────────────────────────────────────── */
function CompetitorPanel() {
  const [sku, setSku] = useState("");
  const [compare, setCompare] = useState(null);
  const [form, setForm] = useState({ retailer: "Amazon AU", price: "", url: "" });
  const [busy, setBusy] = useState(false);

  const lookup = async (skuToFetch) => {
    const v = skuToFetch || sku;
    if (!v) return;
    setBusy(true);
    try {
      const r = await api.get(`/api/plaza/pricing/compare/${encodeURIComponent(v.trim())}`);
      setCompare(r.data);
    } catch (e) {
      setCompare({ error: e.response?.data?.detail || "lookup failed" });
    } finally {
      setBusy(false);
    }
  };

  const addCompetitor = async () => {
    if (!sku || !form.price) return;
    setBusy(true);
    try {
      await api.post("/api/plaza/pricing/competitor", {
        sku: sku.trim(),
        retailer: form.retailer.trim(),
        price_cents: Math.round(parseFloat(form.price) * 100),
        url: form.url.trim() || null,
      });
      setForm({ retailer: "Amazon AU", price: "", url: "" });
      lookup();
    } finally {
      setBusy(false);
    }
  };

  const removeCompetitor = async (retailer) => {
    setBusy(true);
    try {
      await api.delete(`/api/plaza/pricing/competitor?sku=${encodeURIComponent(sku)}&retailer=${encodeURIComponent(retailer)}`);
      lookup();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hpz-admin__panel">
      <div className="hpz-admin__form">
        <label className="full">
          <span>SKU</span>
          <input value={sku} onChange={e => setSku(e.target.value)}
                 placeholder="e.g. AES-T001B"
                 data-testid="admin-competitor-sku" />
        </label>
        <button onClick={() => lookup()} disabled={!sku || busy}
                className="hpz-admin__btn primary"
                data-testid="admin-competitor-lookup">
          {busy ? <Loader2 className="hpz-spin" size={14} /> : "Look up"}
        </button>
      </div>

      {compare?.error && (
        <p className="hpz-admin__error"><AlertCircle size={14} /> {compare.error}</p>
      )}
      {compare && !compare.error && (
        <>
          <div className="hpz-admin__compare-summary">
            <div>
              <strong>Our price:</strong> ${(compare.our_price_cents / 100).toFixed(2)}
            </div>
            <div>
              <strong>Status:</strong>{" "}
              {compare.competitive ? (
                <span className="hpz-admin__badge green">Competitive</span>
              ) : (
                <span className="hpz-admin__badge red">Uncompetitive (hidden)</span>
              )}
            </div>
            {compare.we_are_cheapest && compare.savings_vs_best_competitor_cents > 0 && (
              <div>
                <strong>We save buyers:</strong>{" "}
                <span className="hpz-admin__badge blue">
                  ${(compare.savings_vs_best_competitor_cents / 100).toFixed(2)}
                </span>
              </div>
            )}
          </div>

          <table className="hpz-admin__table" data-testid="admin-competitor-table">
            <thead>
              <tr><th>Retailer</th><th>Price</th><th>URL</th><th></th></tr>
            </thead>
            <tbody>
              {(compare.competitor_prices || []).map(cp => (
                <tr key={cp.retailer}>
                  <td>{cp.retailer}</td>
                  <td>${(cp.price_cents / 100).toFixed(2)}</td>
                  <td>{cp.url ? <a href={cp.url} target="_blank" rel="noopener noreferrer">view</a> : "—"}</td>
                  <td>
                    <button onClick={() => removeCompetitor(cp.retailer)}
                            className="hpz-admin__icon-btn">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="hpz-admin__form">
            <label>
              <span>Retailer</span>
              <input value={form.retailer}
                     onChange={e => setForm({ ...form, retailer: e.target.value })}
                     placeholder="Amazon AU"
                     data-testid="admin-competitor-retailer" />
            </label>
            <label>
              <span>Price (AUD)</span>
              <input type="number" step="0.01" value={form.price}
                     onChange={e => setForm({ ...form, price: e.target.value })}
                     placeholder="129.00"
                     data-testid="admin-competitor-price" />
            </label>
            <label className="full">
              <span>URL (optional)</span>
              <input value={form.url}
                     onChange={e => setForm({ ...form, url: e.target.value })}
                     placeholder="https://amazon.com.au/dp/..."
                     data-testid="admin-competitor-url" />
            </label>
            <button onClick={addCompetitor} disabled={!form.price || busy}
                    className="hpz-admin__btn primary"
                    data-testid="admin-competitor-add">
              <Plus size={14} /> Add competitor
            </button>
          </div>
          <p className="hpz-admin__hint">
            URL scraper (paste-and-extract) is on the roadmap — manual entry only today.
          </p>
        </>
      )}
    </section>
  );
}

/* ─── Freight zones ───────────────────────────────────────────────── */
function FreightPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/api/plaza/freight/config")
       .then(r => setCfg(r.data))
       .catch(() => setCfg({ error: true }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/api/plaza/freight/config", {
        zones: cfg.zones,
        default_zone: cfg.default_zone,
        handling_fee_cents: cfg.handling_fee_cents || 0,
      });
      alert("Freight config saved.");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Replace all zones with AU Post defaults? Your edits will be lost.")) return;
    setSaving(true);
    try {
      const r = await api.post("/api/plaza/freight/reset-defaults");
      setCfg(r.data.config);
    } finally {
      setSaving(false);
    }
  };

  const updateZone = (idx, field, val) => {
    const next = { ...cfg, zones: cfg.zones.map((z, i) =>
      i === idx ? { ...z, [field]: field.endsWith("_cents") ? parseInt(val) || 0 : val } : z
    )};
    setCfg(next);
  };

  if (!cfg) return <div className="hpz-admin__loading"><Loader2 className="hpz-spin" /></div>;
  if (cfg.error) return (
    <section className="hpz-admin__panel"><p className="hpz-admin__error">
      <AlertCircle size={14} /> Failed to load freight config. Please re-authenticate.
    </p></section>
  );

  return (
    <section className="hpz-admin__panel">
      <p className="hpz-admin__notice">
        <AlertCircle size={14} />
        DSZ does <strong>not</strong> publish freight rates via API — we calculate
        freight ourselves using DSZ&apos;s per-product weight + dim data and the
        zone table below. Tune to match your real-world DSZ-invoiced costs
        after one month of orders.
      </p>

      <div className="hpz-admin__form-row">
        <label>
          <span>Default zone (when postcode doesn&apos;t match)</span>
          <select value={cfg.default_zone}
                  onChange={e => setCfg({ ...cfg, default_zone: e.target.value })}
                  data-testid="admin-default-zone">
            {cfg.zones.map(z => (
              <option key={z.code} value={z.code}>{z.code} — {z.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Handling fee (cents per order)</span>
          <input type="number" min="0" value={cfg.handling_fee_cents || 0}
                 onChange={e => setCfg({ ...cfg, handling_fee_cents: parseInt(e.target.value) || 0 })}
                 data-testid="admin-handling-fee" />
        </label>
      </div>

      {cfg.zones.map((z, idx) => (
        <div key={z.code} className="hpz-admin__zone-card"
             data-testid={`admin-zone-${z.code}`}>
          <h3>{z.code} — {z.label}</h3>
          <div className="hpz-admin__form-row">
            <label>
              <span>Base (cents)</span>
              <input type="number" min="0" value={z.base_cents}
                     onChange={e => updateZone(idx, "base_cents", e.target.value)} />
            </label>
            <label>
              <span>Per kg (cents)</span>
              <input type="number" min="0" value={z.per_kg_cents}
                     onChange={e => updateZone(idx, "per_kg_cents", e.target.value)} />
            </label>
            <label>
              <span>Free over (cents)</span>
              <input type="number" min="0" value={z.free_over_cents}
                     onChange={e => updateZone(idx, "free_over_cents", e.target.value)} />
            </label>
          </div>
          <label className="full">
            <span>Postcode ranges (comma-separated, e.g. <code>1000-2599, 3000-3207</code>)</span>
            <textarea rows="2" value={(z.postcodes || []).join(", ")}
                      onChange={e => updateZone(idx, "postcodes",
                        e.target.value.split(",").map(s => s.trim()).filter(Boolean))} />
          </label>
        </div>
      ))}

      <div className="hpz-admin__actions">
        <button onClick={save} disabled={saving} className="hpz-admin__btn primary"
                data-testid="admin-save-freight">
          {saving ? <Loader2 className="hpz-spin" size={14} /> : <Save size={14} />}
          Save freight config
        </button>
        <button onClick={resetDefaults} disabled={saving} className="hpz-admin__btn"
                data-testid="admin-reset-freight">
          <RefreshCw size={14} /> Reset to AU Post defaults
        </button>
      </div>
    </section>
  );
}

/* ─── Holiday Themes ──────────────────────────────────────────────── */
function ThemesPanel() {
  const [settings, setSettings] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [activePreview, setActivePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cycleSel, setCycleSel] = useState(new Set());
  const [cycleSec, setCycleSec] = useState(5);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/api/plaza/themes/settings"),
      api.get("/api/plaza/themes/catalog"),
      api.get("/api/plaza/themes/active"),
    ]).then(([s, c, a]) => {
      setSettings(s.data);
      setCatalog(c.data.themes || []);
      setActivePreview(a.data.theme || null);
    }).catch(() => setSettings({ error: true }));
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await api.post("/api/plaza/themes/settings", {
        auto_apply_enabled: next.auto_apply_enabled,
        override_theme_key: next.override_theme_key || null,
      });
      setSettings(r.data.settings);
      // Refresh active preview
      const a = await api.get("/api/plaza/themes/active");
      setActivePreview(a.data.theme || null);
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="hpz-admin__loading"><Loader2 className="hpz-spin" /></div>;
  if (settings.error) return (
    <section className="hpz-admin__panel"><p className="hpz-admin__error">
      <AlertCircle size={14} /> Failed to load theme settings. Please re-authenticate.
    </p></section>
  );

  return (
    <section className="hpz-admin__panel" data-testid="admin-themes-panel">
      <p className="hpz-admin__notice">
        <AlertCircle size={14} />
        Holiday themes auto-apply on the public storefront based on the
        calendar (Christmas, Halloween, Australia Day, Black Friday, etc.).
        Toggle the master switch to opt out, or pin a specific theme.
      </p>

      <div className="hpz-admin__form">
        <label className="hpz-admin__toggle">
          <input
            type="checkbox"
            checked={settings.auto_apply_enabled}
            onChange={(e) => save({ ...settings,
                                      auto_apply_enabled: e.target.checked })}
            disabled={saving}
            data-testid="admin-theme-auto-toggle"
          />
          <span><strong>Auto-apply holiday themes</strong> based on today&apos;s date</span>
        </label>

        <label>
          <span>Override (pin a specific theme — overrides the calendar)</span>
          <select
            value={settings.override_theme_key || ""}
            onChange={(e) => save({ ...settings,
                                      override_theme_key: e.target.value || null })}
            disabled={saving}
            data-testid="admin-theme-override"
          >
            <option value="">— Auto (calendar-driven) —</option>
            {catalog.map(t => (
              <option key={t.key} value={t.key}>
                {t.banner_emoji} {t.name} {t.tier === "premium" ? "(premium)" : ""}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="hpz-admin__theme-preview"
            data-testid="admin-theme-active-preview">
        <h3>Currently active on storefront</h3>
        {activePreview ? (
          <div
            className="hpz-admin__theme-card"
            style={{
              background: activePreview.palette.banner_bg,
              color: activePreview.palette.banner_fg,
            }}
          >
            <span style={{ fontSize: 28 }}>{activePreview.banner_emoji}</span>
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 16 }}>
                {activePreview.name}
              </p>
              <p style={{ margin: "4px 0 0", fontSize: 12, opacity: 0.85 }}>
                {activePreview.banner_label}
              </p>
            </div>
          </div>
        ) : (
          <p style={{ color: "#6b7280", fontSize: 13 }}>
            No theme is active right now — storefront uses the default palette.
          </p>
        )}
      </div>

      <h3 style={{ marginTop: 24, marginBottom: 12 }}>Theme catalog</h3>
      <div className="hpz-admin__theme-grid">
        {catalog.map(t => {
          const inCycle = cycleSel.has(t.key);
          return (
            <div key={t.key}
                  className={`hpz-admin__theme-card hpz-admin__theme-card--tile ${inCycle ? "selected" : ""}`}
                  data-testid={`admin-theme-tile-${t.key}`}
                  style={{
                    background: t.palette.banner_bg,
                    color: t.palette.banner_fg,
                  }}>
              <button
                className="hpz-admin__theme-pick"
                data-testid={`admin-theme-pick-${t.key}`}
                onClick={() => {
                  setCycleSel(prev => {
                    const next = new Set(prev);
                    if (next.has(t.key)) next.delete(t.key);
                    else next.add(t.key);
                    return next;
                  });
                }}
                style={{
                  color: t.palette.banner_fg,
                  background: inCycle
                    ? "rgba(255,255,255,0.30)"
                    : "rgba(255,255,255,0.10)",
                }}
                title={inCycle ? "Remove from cycle" : "Add to cycle"}
                aria-label={inCycle ? "Remove from cycle" : "Add to cycle"}
              >{inCycle ? "✓" : "+"}</button>
              <span style={{ fontSize: 22 }}>{t.banner_emoji}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>
                  {t.name}
                </p>
                <p style={{ margin: "2px 0 0", fontSize: 11, opacity: 0.85 }}>
                  {t.tier === "premium"
                    ? "Premium · vendor upgrade"
                    : "Auto · " + (t.match_rules?.length || 0) + " rule(s)"}
                </p>
              </div>
              <a
                href={`/plaza/shop?preview_theme=${encodeURIComponent(t.key)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hpz-admin__theme-preview-btn"
                data-testid={`admin-theme-preview-${t.key}`}
                style={{
                  background: t.palette.banner_fg,
                  color: t.palette.banner_bg,
                }}
                title="Open storefront preview in a new tab"
              >
                Preview ↗
              </a>
            </div>
          );
        })}
      </div>

      {/* ─── Cycle builder (investor-demo helper) ─── */}
      <div className="hpz-admin__cycle-builder"
            data-testid="admin-theme-cycle-builder">
        <h3>Build a recording cycle</h3>
        <p className="hpz-admin__cycle-help">
          Pick themes above using the <strong>+</strong> button, choose a
          dwell time, then open the cycle URL in a new tab for a hands-off
          screen-capture. Themes will auto-rotate and the banner fades
          between palettes.
        </p>
        <div className="hpz-admin__cycle-row">
          <label>
            Dwell (seconds)
            <input type="number" min="2" max="30"
                    value={cycleSec}
                    onChange={(e) => setCycleSec(Math.max(2,
                                          Math.min(30, +e.target.value || 5)))}
                    data-testid="admin-cycle-sec" />
          </label>
          <div className="hpz-admin__cycle-selected"
                data-testid="admin-cycle-selected">
            Selected: <strong>{cycleSel.size}</strong> theme(s)
            {cycleSel.size > 0 && (
              <button onClick={() => setCycleSel(new Set())}
                       className="hpz-admin__cycle-clear"
                       data-testid="admin-cycle-clear">Clear</button>
            )}
          </div>
        </div>
        {cycleSel.size > 0 && (() => {
          const orderedKeys = catalog
            .map(t => t.key)
            .filter(k => cycleSel.has(k));
          const cycleUrl =
            `/plaza/shop?theme_cycle=${encodeURIComponent(orderedKeys.join(","))}` +
            `&cycle_ms=${cycleSec * 1000}`;
          return (
            <>
              <div className="hpz-admin__cycle-url"
                    data-testid="admin-cycle-url">
                <code>{cycleUrl}</code>
                <button onClick={() => {
                  navigator.clipboard.writeText(window.location.origin + cycleUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                  data-testid="admin-cycle-copy">
                  {copied ? "✓ Copied" : "Copy"}
                </button>
              </div>
              <a href={cycleUrl} target="_blank" rel="noopener noreferrer"
                  className="hpz-admin__cycle-launch"
                  data-testid="admin-cycle-launch">
                Launch cycle in new tab ↗
              </a>
            </>
          );
        })()}
      </div>
    </section>
  );
}
